import { useEffect, useRef, useState, type CSSProperties } from "react";
import {
  ArrowLeft,
  Volume2,
  VolumeX,
  Music2,
  Lightbulb,
  ArrowRight,
  Check,
  RotateCcw,
  X,
} from "lucide-react";
import {
  type Session,
  type Task,
  type Answer,
  type Event,
  type InteractionResponse,
  SCENES,
  rhyme,
  strategy,
  answerOf,
  interactionIsCorrect,
  makeTask,
} from "../shared/game";
import { type IslandStore } from "./store";
import { Animal } from "./Art";
import { speak, speakSequence, stopAudio } from "./audio";
import {
  mathFeedback,
  mathFeedbackKey,
  praiseFeedback,
  praiseAudioKey,
  hintAudioKey,
} from "../shared/language";
import { CourseInteraction, initialInteractionResponse } from "./course-interactions";
import "./course-interactions.css";
const names: Record<Task["kind"], string> = {
  group: "摆一摆，想明白",
  array: "把数量拼出来",
  jump: "跟着泡泡跳一跳",
  recall: "试试自己的本领",
  application: "帮朋友解决问题",
  missing: "找回藏起来的数",
  count: "发现你的本领",
  addition: "收集小贝壳",
  subtraction: "分享小贝壳",
  place: "十根捆成一捆",
  share: "帮小兔公平分点心",
  choice: "小岛数学侦探",
  interactive: "动手试一试",
};
const templateNames: Record<string, string> = {
  "SORT-COUNT": "分一分，数一数", "SORT-REFINE": "换个标准再分", "GROUPS-TO-MUL": "摆出相同的几组",
  "ARRAY-TWO-VIEWS": "行和列都看一看", "FACT-MIXED": "混合顺序想口诀", "PARTIAL-ARRAY": "补全不完整的点阵",
  "WORDING-DISTINCTION": "把话说成算式", "DIV-SHARE": "平均分到每一份", "DIV-GROUPS": "固定每份数一数有几份",
  "FACT-FAMILY": "乘除关系配一配", "DIV-MODEL-CHOICE": "每份固定，看看有几份", "MAP-ROTATE": "转一转小兔看方向",
  "MAP-ROUTE": "按方向一步步走", "MAP-RELATION": "从小兔的方向看", "BODY-MEASURE": "家里找一把身体尺",
  "UNIT-ESTIMATE": "先估一估再选单位", "TOOL-CHOICE": "给长度挑工具", "RULER-START-END": "从起点量到终点",
  "RULER-READ": "看尺子读出长度", "SEGMENT-DRAW": "用尺画一条线段", "BUILD-FACT-PATTERN": "拆一拆，推口诀",
  "TWO-STEP": "分两步解决委托", "ERROR-DETECTIVE": "找出伙伴算错的地方", "WORD-PROBLEM-SELECT": "先想题目要找什么",
};
const confettiPieces = Array.from({ length: 22 }, (_, i) => i);
function Celebration({ active }: { active: boolean }) {
  if (!active) return null;
  return (
    <div className="confetti-layer" aria-hidden="true">
      {confettiPieces.map((piece) => (
        <i key={piece} style={{ "--confetti-index": piece } as CSSProperties} />
      ))}
    </div>
  );
}
function wrongGuidance(task: Task, relation: string) {
  switch (task.interaction?.type) {
    case "sort": return "再看看选的标准有没有从头到尾一样。每件物品都要放进合适的篮子。";
    case "partial-array": return "完整的几排先不变，再看旁边是多了还是少了。";
    case "group-count": return "每份数量固定，放满一份再继续，最后数一数有几份。";
    case "multi-step": return "把第一步算出的总数写下来，再处理下一步。";
    case "compass": return "先看地图上的北方，再想小兔转身后自己的左右。";
    case "route": return "从起点开始，一次走一格；走错了可以退回来。";
    case "estimate": return "找一个熟悉的长度作参照，再检查单位和工具是否合适。";
    case "ruler": return "看看尺子的起点和终点。不是从0开始，就用终点减起点。";
    case "segment": return "线段要直直的，两端各有一个端点；再核对长度。";
    case "fact-family": return "想想这两道除法，能不能分别用同一句乘法口诀来检查。";
    case "body-measure": return "请确认量的是同一处；每个人的一拃可能不一样长。";
  }
  if (task.kind === "share") return "先看看每个盒子的数量是不是一样。需要的话，可以拿回来重新分。";
  if (task.kind === "application" && relation !== "multiply") return "先看每份是不是一样多，再决定要把哪些份数算进去。";
  if (task.kind === "group" || task.kind === "array") return "还差一点：看看每一组的数量。";
  return "没关系，再想一想。你可以请泡泡帮忙。";
}
type Draft = {
  counts: number[];
  hops: number;
  input: string;
  attempt: number;
  first: number | null;
  hint: boolean;
  feedback: "wrong" | "right" | null;
  relation: string;
  helpLevel: 0 | 1 | 2;
  interactionData: InteractionResponse | null;
  orientation: "rows" | "columns";
  started: number;
};
const draftKey = (s: Session, t: Task) => `island-draft-${s.id}-${t.id}`;
function initial(s: Session, t: Task, events: Event[]): Draft {
  const emptyCounts = t.kind === "share" && t.shareMode === "group-count"
    ? []
    : Array(t.a).fill(0);
  try {
    const value = JSON.parse(localStorage.getItem(draftKey(s, t)) || "null");
    if (value) return {
      ...value,
      helpLevel: value.helpLevel ?? (value.hint ? 1 : 0),
      interactionData: value.interactionData ?? (t.interaction ? initialInteractionResponse(t.interaction) : null),
      orientation: value.orientation === "columns" ? "columns" : "rows",
    };
  } catch {}
  const prior = events
    .filter((e) => e.type === "answer")
    .map((e) => e.data as unknown as Answer)
    .filter((a) => a.session === s.id && a.task.id === t.id)
    .at(-1);
  if (prior?.correct)
    return {
      counts: t.shareMode === "group-count" ? Array(t.b).fill(t.a) : Array(t.a).fill(t.b),
      hops: t.a,
      input: String(t.answer),
      attempt: prior.attempt,
      first: prior.firstAnswer,
      hint: prior.hinted,
      feedback: "right",
      relation: "multiply",
      helpLevel: prior.evidence?.helpLevel ?? (prior.hinted ? 1 : 0),
      interactionData: t.interaction ? initialInteractionResponse(t.interaction) : null,
      orientation: "rows",
      started: Date.now(),
    };
  if (prior)
    return {
      counts: emptyCounts,
      hops: 0,
      input: prior.value >= 0 ? String(prior.value) : "",
      attempt: prior.attempt,
      first: prior.firstAnswer,
      hint: prior.hinted || prior.attempt >= 2,
      feedback: "wrong",
      relation: prior.relation || "",
      helpLevel: prior.evidence?.helpLevel ?? (prior.hinted || prior.attempt >= 2 ? 1 : 0),
      interactionData: t.interaction ? initialInteractionResponse(t.interaction) : null,
      orientation: "rows",
      started: Date.now(),
    };
  return {
    counts: emptyCounts,
    hops: 0,
    input: "",
    attempt: 0,
    first: null,
    hint: false,
    feedback: null,
    relation: "",
    helpLevel: 0,
    interactionData: t.interaction ? initialInteractionResponse(t.interaction) : null,
    orientation: "rows",
    started: Date.now(),
  };
}
export function Lesson({
  store,
  voiceMuted,
  musicMuted,
  onVoiceMutedChange,
  onMusicMutedChange,
  onExit,
  onFinish,
}: {
  store: IslandStore;
  voiceMuted: boolean;
  musicMuted: boolean;
  onVoiceMutedChange: (muted: boolean) => void;
  onMusicMutedChange: (muted: boolean) => void;
  onExit: () => void;
  onFinish: (s: Session) => void;
}) {
  const s = store.session!;
  const t = s.tasks[s.index];
  return (
    <LessonStep
      key={`${s.id}-${t.id}`}
      store={store}
      voiceMuted={voiceMuted}
      musicMuted={musicMuted}
      onVoiceMutedChange={onVoiceMutedChange}
      onMusicMutedChange={onMusicMutedChange}
      session={s}
      task={t}
      onExit={onExit}
      onFinish={onFinish}
    />
  );
}
function LessonStep({
  store,
  voiceMuted,
  musicMuted,
  onVoiceMutedChange,
  onMusicMutedChange,
  session: s,
  task: t,
  onExit,
  onFinish,
}: {
  store: IslandStore;
  voiceMuted: boolean;
  musicMuted: boolean;
  onVoiceMutedChange: (muted: boolean) => void;
  onMusicMutedChange: (muted: boolean) => void;
  session: Session;
  task: Task;
  onExit: () => void;
  onFinish: (s: Session) => void;
}) {
  const [d, setD] = useState<Draft>(() => initial(s, t, store.events));
  const state = useRef(d);
  state.current = d;
  const locked = useRef(false);
  const feedbackRef = useRef<HTMLDivElement>(null);
  const [exit, setExit] = useState(false);
  const [audioError, setAudioError] = useState(false);
  const [selected, setSelected] = useState(0);
  const scene = SCENES[t.scene];
  const taskName = t.templateId ? templateNames[t.templateId] || names[t.kind] : names[t.kind];
  const visual = ["group", "array", "jump", "share", "interactive"].includes(t.kind);
  const assessed = ["assessment", "baseline"].includes(s.mode);
  const successPraise = praiseFeedback(t, d.hint || d.attempt > 1);
  const successAudio = [
    { key: mathFeedbackKey(t), text: mathFeedback(t) },
    { key: praiseAudioKey(t, d.hint || d.attempt > 1), text: successPraise },
  ];
  const total = d.counts.reduce((a, b) => a + b, 0);
  useEffect(() => {
    try {
      localStorage.setItem(draftKey(s, t), JSON.stringify(d));
    } catch {}
  }, [d, s.id, t.id]);
  useEffect(() => {
    locked.current = false;
  }, [d]);
  useEffect(() => {
    if (d.feedback !== "right") return;
    const id = window.requestAnimationFrame(() =>
      feedbackRef.current?.scrollIntoView({
        behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
          ? "auto"
          : "smooth",
        block: "center",
      }),
    );
    return () => window.cancelAnimationFrame(id);
  }, [d.feedback]);
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" });
    void speak(t.question, t.audio).then((ok) => setAudioError(!ok));
    return () => stopAudio();
  }, [t.id]);
  const update = (patch: Partial<Draft>) =>
    setD((prev) => ({ ...prev, ...patch }));
  const addObject = (index: number) => {
    if (d.feedback === "right") return;
    setD((prev) => ({
      ...prev,
      feedback: null,
      counts: prev.counts.map((v, i) =>
        i === index ? Math.min(t.kind === "share" ? t.b : 9, v + 1) : v,
      ),
    }));
  };
  const check = () => {
    if (locked.current || state.current.feedback === "right") return;
    locked.current = true;
    const current = state.current;
    let value = t.kind === "interactive"
      ? t.interaction && interactionIsCorrect(t.interaction, current.interactionData || undefined) ? answerOf(t) : -1
      : visual
      ? t.kind === "jump"
        ? current.hops * t.b
        : t.kind === "share"
          ? t.shareMode === "group-count"
            ? current.counts.length === t.b && current.counts.every((n) => n === t.a)
              ? answerOf(t)
              : -1
            : current.counts.every((n) => n === t.b)
            ? answerOf(t)
            : -1
          : current.counts.reduce((a, b) => a + b, 0)
      : Number(current.input);
    if (!visual && !current.input) {
      locked.current = false;
      return;
    }
    const modelCorrect =
      t.kind === "interactive"
        ? Boolean(t.interaction && interactionIsCorrect(t.interaction, current.interactionData || undefined))
        : t.kind === "jump"
        ? current.hops === t.a
        : t.kind === "share" && t.shareMode === "group-count"
          ? current.counts.length === t.b && current.counts.every((n) => n === t.a)
          : current.counts.every((n) => n === t.b);
    const enteredAnswer = value;
    if (
      (visual && !modelCorrect) ||
      (t.kind === "application" && current.relation !== "multiply")
    )
      value = -1;
    const correct = value === answerOf(t);
    const attempt = current.attempt + 1;
    const first = current.first ?? enteredAnswer;
    const event: Answer = {
      task: t,
      value,
      firstAnswer: first,
      correct,
      hinted: current.hint,
      attempt,
      duration: Math.min(86400000, Date.now() - current.started),
      session: s.id,
      enteredAnswer,
      relation: current.relation,
      ...(t.evidenceRole ? {
        evidence: {
          version: 3 as const,
          responseMode: t.kind === "choice" ? "selection" as const : t.interaction?.type === "multi-step" || t.interaction?.type === "estimate" || !visual ? "numeric" as const : "manipulation" as const,
          helpLevel: current.helpLevel,
          explanation: correct && t.evidenceRole !== "recognition" ? "sentence-frame" as const : "none" as const,
          response: t.interaction
            ? current.interactionData || undefined
            : t.kind === "group"
              ? { type: "groups" as const, counts: [...current.counts] }
              : t.kind === "array"
                ? { type: "array" as const, counts: [...current.counts], orientation: current.orientation }
                : t.kind === "share"
                  ? { type: "share" as const, counts: [...current.counts] }
                  : t.kind === "jump"
                    ? { type: "jump" as const, hops: current.hops }
                    : { type: "numeric" as const, value: Number(current.input), relation: current.relation },
        },
      } : {}),
    };
    store.add("answer", event as unknown as Record<string, unknown>);
    const hint = current.hint || (!correct && attempt >= 2);
    const helpLevel = current.helpLevel || (!correct && attempt >= 2 ? 1 : 0);
    update({ attempt, first, feedback: correct ? "right" : "wrong", hint, helpLevel: helpLevel as 0 | 1 | 2 });
    if (correct) {
      if (typeof navigator !== "undefined" && "vibrate" in navigator)
        navigator.vibrate?.([18, 24, 42]);
      void speakSequence(successAudio).then((ok) => setAudioError(!ok));
    } else
      void speak(
        attempt >= 2
          ? "我们把它摆出来，一起想一想。"
          : "再看看数量，我们慢慢来。",
        "retry",
      );
  };
  const proceed = () => {
    stopAudio();
    try {
      localStorage.removeItem(draftKey(s, t));
    } catch {}
    let tasks = s.tasks;
    if (
      !assessed &&
      !visual &&
      d.attempt >= 3 &&
      !t.id.includes("-confirm") &&
      !t.foundation &&
      t.kind !== "choice" && t.kind !== "interactive"
    ) {
      const variant = makeTask(
        "application",
        t.b,
        t.a,
        t.scene === "harbor" ? "garden" : "harbor",
        `${t.id}-confirm`,
      );
      variant.practiceOnly = true;
      tasks = [
        ...tasks.slice(0, s.index + 1),
        variant,
        ...tasks.slice(s.index + 1),
      ];
    }
    if (s.index + 1 >= tasks.length) {
      onFinish({ ...s, tasks });
      return;
    }
    store.setSession({ ...s, tasks, index: s.index + 1 });
    if (Date.now() - s.started >= 10 * 60000) onExit();
  };
  useEffect(() => {
    const listener = (e: KeyboardEvent) => {
      if (exit || e.target instanceof HTMLInputElement) return;
      if (e.key === "Enter" && e.target instanceof HTMLButtonElement) return;
      if (e.key === "Enter") {
        e.preventDefault();
        if (state.current.feedback === "right") proceed();
        else check();
      } else if (
        state.current.feedback !== "right" &&
        !visual &&
        /^\d$/.test(e.key)
      )
        setD((prev) => ({
          ...prev,
          input: (prev.input + e.key).slice(0, 3),
          feedback: null,
        }));
      else if (
        state.current.feedback !== "right" &&
        e.key === "Backspace" &&
        !visual
      ) {
        e.preventDefault();
        setD((prev) => ({ ...prev, input: prev.input.slice(0, -1) }));
      }
    };
    window.addEventListener("keydown", listener);
    return () => window.removeEventListener("keydown", listener);
  });
  const num = (v: string) => {
    if (d.feedback === "right") return;
    update({
      input: v === "⌫" ? d.input.slice(0, -1) : (d.input + v).slice(0, 3),
      feedback: null,
    });
  };
  return (
    <main className="lesson-page">
      <header className="lesson-header">
        <button
          className="icon-button"
          aria-label="暂存并退出"
          onClick={() => setExit(true)}
        >
          <ArrowLeft />
        </button>
        <div className="lesson-progress">
          <div className="lesson-progress-label">
            <span>
              {s.mode === "baseline"
                ? "第一次见面的小委托"
                : s.mode === "assessment"
                  ? "本领小观察"
                  : s.mode === "review"
                    ? "伙伴的复习来信"
                : taskName}
            </span>
            <span>
              {s.index + 1} / {s.tasks.length}
            </span>
          </div>
          <div className="progress-track">
            <div style={{ width: `${(s.index / s.tasks.length) * 100}%` }} />
          </div>
        </div>
        <button
          className="icon-button"
          aria-label="重听题目"
          onClick={() =>
            void speak(t.question, t.audio).then((ok) => setAudioError(!ok))
          }
        >
          <Volume2 />
        </button>
        <details className="lesson-audio-settings">
          <summary className="icon-button" aria-label="调整语音和音乐"><Music2 size={18} /></summary>
          <div className="lesson-audio-panel">
            <button type="button" className="secondary" aria-pressed={!voiceMuted} onClick={() => onVoiceMutedChange(!voiceMuted)}>{voiceMuted ? <VolumeX size={17} /> : <Volume2 size={17} />}伙伴语音{voiceMuted ? "已关闭" : "已开启"}</button>
            <button type="button" className="secondary" aria-pressed={!musicMuted} onClick={() => onMusicMutedChange(!musicMuted)}><Music2 size={17} />背景音乐{musicMuted ? "已关闭" : "已开启"}</button>
          </div>
        </details>
      </header>
      <section className="lesson-content">
        <Celebration
          active={d.feedback === "right" && s.index === s.tasks.length - 1}
        />
        <div className="lesson-guide">
          <Animal
            type={
              t.scene === "breakfast"
                ? "rabbit"
                : t.scene === "garden"
                  ? "panda"
                  : "bear"
            }
            size={90}
          />
          <div>
            <span className="eyebrow">
              {scene.friend}的小委托 · {taskName}
            </span>
            <h1>{t.question}</h1>
          </div>
        </div>
        {audioError && (
          <p className="note">这道新委托的 MiniMax 语音尚未生成，图文和操作可以继续。</p>
        )}
        {t.kind === "share" && (
          <div className="manipulative picnic-board">
            <p className="instruction">
              {t.shareMode === "group-count"
                ? `每份固定放${t.a}块。每次组成一份，再看看一共能分几份。`
                : "先选一块点心，再点一个盒子放进去。可以拿回来重分，直到每盒一样多。"}
            </p>
            <div className="picnic-items" aria-label="还未放进盒子的点心">
              {Array.from({ length: Math.max(0, t.a * t.b - total) }, (_, i) => (
                <span key={i} aria-hidden="true">🥟</span>
              ))}
              <strong>还剩 {t.a * t.b - total} 块</strong>
            </div>
            {t.shareMode === "group-count" ? (
              <>
                <div className="picnic-boxes">
                  {d.counts.map((count, index) => (
                    <div className="picnic-box-wrap" key={index}>
                      <div className="picnic-box filled">
                        <span>第{index + 1}份</span>
                        <span className="picnic-box-items">
                          {Array.from({ length: count }, (_, i) => <i key={i}>🥟</i>)}
                        </span>
                        <strong>{count} 块</strong>
                      </div>
                    </div>
                  ))}
                </div>
                <button
                  className="primary full"
                  disabled={d.feedback === "right" || total >= t.a * t.b}
                  onClick={() => update({ counts: [...d.counts, t.a], feedback: null })}
                >
                  每份放 {t.a} 块 <span aria-hidden>＋</span>
                </button>
                <button
                  className="secondary full"
                  disabled={d.feedback === "right" || d.counts.length === 0}
                  onClick={() => update({ counts: d.counts.slice(0, -1), feedback: null })}
                >拿回最后一份</button>
                <div className="math-caption">已分成 {d.counts.length} 份 · 每份 {t.a} 块</div>
              </>
            ) : (
              <>
                <div className="picnic-boxes">
                  {d.counts.map((count, index) => (
                    <div className="picnic-box-wrap" key={index}>
                      <button
                        className={`picnic-box ${count === t.b ? "filled" : ""}`}
                        disabled={d.feedback === "right" || total >= t.a * t.b}
                        onClick={() => addObject(index)}
                        aria-label={`第${index + 1}个盒子，有${count}块，点击放一块`}
                      >
                        <span>第{index + 1}盒</span>
                        <span className="picnic-box-items">
                          {Array.from({ length: count }, (_, i) => <i key={i}>🥟</i>)}
                          {!count && <small>点心放这里</small>}
                        </span>
                        <strong>{count} 块</strong>
                      </button>
                      <button
                        className="text-button"
                        disabled={d.feedback === "right" || count === 0}
                        onClick={() => update({ counts: d.counts.map((n, i) => i === index ? n - 1 : n), feedback: null })}
                      >拿回一块</button>
                    </div>
                  ))}
                </div>
              <div className="math-caption">
                {(t as Task).shareMode === "group-count" ? "总数 ÷ 每份数量 = 份数" : "总数 ÷ 盒子数 = 每盒数量"}
              </div>
              </>
            )}
          </div>
        )}
        {t.kind === "group" && (
          <div className="manipulative">
            <div className="instruction">
              <span>
                点一组，放上一{scene.classifier}
                {scene.item}
              </span>
              <button
                className="text-button"
                onClick={() =>
                  update({ counts: Array(t.a).fill(0), feedback: null })
                }
                disabled={d.feedback === "right"}
              >
                <RotateCcw size={15} />
                重新摆
              </button>
            </div>
            <div className="group-grid">
              {d.counts.map((count, i) => (
                <div className="group-wrap" key={i}>
                  <button
                    className={`group-table ${count === t.b ? "filled" : ""}`}
                    onClick={() => addObject(i)}
                    disabled={d.feedback === "right"}
                    aria-label={`第${i + 1}${scene.unit}，已有${count}个，点击添加`}
                  >
                    <span className="table-number">{i + 1}</span>
                    <div className="objects">
                      {Array.from({ length: count }, (_, j) => (
                        <span className="object" key={j}>
                          {scene.emoji}
                        </span>
                      ))}
                      {count === 0 && <span className="ghost-plus">＋</span>}
                    </div>
                    <span className="table-count">{count} 个</span>
                  </button>
                  <button
                    className="remove-object"
                    aria-label={`第${i + 1}组拿走一个`}
                    disabled={!count || d.feedback === "right"}
                    onClick={() =>
                      update({
                        counts: d.counts.map((v, k) =>
                          i === k ? Math.max(0, v - 1) : v,
                        ),
                        feedback: null,
                      })
                    }
                  >
                    拿走一个
                  </button>
                </div>
              ))}
            </div>
            {t.a > 1 && (
              <button
                className="secondary copy-group"
                disabled={
                  d.feedback === "right" ||
                  d.counts[0] !== t.b ||
                  !d.counts.slice(1).includes(0)
                }
                onClick={() => {
                  const target = d.counts.findIndex((n, i) => i > 0 && n === 0);
                  update({
                    counts: d.counts.map((n, i) =>
                      i === target ? d.counts[0] : n,
                    ),
                    feedback: null,
                  });
                }}
              >
                照第一组，再摆一份（{t.b}个）
              </button>
            )}
            <div className="math-caption">
              {d.counts.join(" ＋ ")} <span>＝ {total}</span>
            </div>
          </div>
        )}
        {t.kind === "array" && (
          <div className="manipulative">
            <div className="array-orientation" role="group" aria-label="点阵观察方向">
              {(["rows", "columns"] as const).map((orientation) => <button type="button" key={orientation} className={d.orientation === orientation ? "selected" : "secondary"} aria-pressed={d.orientation === orientation} disabled={d.feedback === "right"} onClick={() => { update({ orientation }); setSelected(0); }}>{orientation === "rows" ? "按行看" : "按列看"}</button>)}
            </div>
            <p className="instruction">
              {d.orientation === "rows" ? "先选一排，再点「添一颗」。每排都要一样多。" : "现在按列观察。每一列里有几颗？列数变了，总数会变吗？"}
            </p>
            <div className={`array-board ${d.orientation === "columns" ? "columns-view" : ""}`}>
              {d.counts.map((n, i) => (
                <button
                  key={i}
                  className={`array-row ${selected === i ? "selected" : ""}`}
                  onClick={() => setSelected(i)}
                  aria-label={`选择第${i + 1}排，已有${n}颗`}
                >
                  <span className="row-index">{i + 1}</span>
                  {Array.from({ length: 9 }, (_, j) => (
                    <span
                      key={j}
                      className={`array-dot ${j < n ? "active" : ""}`}
                    />
                  ))}
                  <span className="row-total">{n}</span>
                </button>
              ))}
            </div>
            <div className="array-controls">
              <button
                className="secondary"
                disabled={d.feedback === "right" || !d.counts[selected]}
                onClick={() =>
                  update({
                    counts: d.counts.map((n, i) =>
                      i === selected ? Math.max(0, n - 1) : n,
                    ),
                    feedback: null,
                  })
                }
              >
                拿走一颗
              </button>
              <button
                className="primary"
                disabled={d.feedback === "right" || d.counts[selected] >= 9}
                onClick={() => addObject(selected)}
              >
                第{selected + 1}排添一颗 ＋
              </button>
            </div>
            {t.a > 1 && (
              <button
                className="secondary copy-group"
                disabled={
                  d.feedback === "right" ||
                  d.counts[0] !== t.b ||
                  !d.counts.slice(1).includes(0)
                }
                onClick={() => {
                  const target = d.counts.findIndex((n, i) => i > 0 && n === 0);
                  update({
                    counts: d.counts.map((n, i) =>
                      i === target ? d.counts[0] : n,
                    ),
                    feedback: null,
                  });
                  setSelected(target);
                }}
              >
                照第一排，再拼一排（{t.b}颗）
              </button>
            )}
            <p className="math-caption">
              {t.a} 排 × 每排 {t.b} 个
            </p>
          </div>
        )}
        {t.kind === "jump" && (
          <div className="manipulative jump-board">
            <div className="jump-pet">
              <Animal size={90} />
              <div>
                每次跳 <strong>{t.b}</strong> 格
              </div>
            </div>
            <div className="number-path">
              {Array.from({ length: t.a + 1 }, (_, i) => (
                <div
                  key={i}
                  className={`jump-stone ${i <= d.hops ? "visited" : ""} ${i === d.hops ? "current" : ""}`}
                >
                  <span>{i * t.b}</span>
                  {i === d.hops && <span className="stone-mark">✦</span>}
                </div>
              ))}
            </div>
            <p>
              已经跳了 <strong>{d.hops}</strong> 次，目标是{" "}
              <strong>{t.a}</strong> 次
            </p>
            <div className="array-controls">
              <button
                className="secondary"
                onClick={() => update({ hops: 0, feedback: null })}
                disabled={d.feedback === "right"}
              >
                从头跳
              </button>
              <button
                className="primary"
                onClick={() =>
                  update({
                    hops: Math.min(t.a + 1, d.hops + 1),
                    feedback: null,
                  })
                }
                disabled={d.feedback === "right" || d.hops >= t.a + 1}
              >
                向前跳 {t.b} 格 <ArrowRight size={18} />
              </button>
            </div>
          </div>
        )}
        {t.kind === "interactive" && t.interaction && (
          <CourseInteraction
            spec={t.interaction}
            value={d.interactionData || undefined}
            disabled={d.feedback === "right"}
            onChange={(interactionData) => update({ interactionData, feedback: null })}
          />
        )}
        {!visual && (
          <div className="answer-area">
            {t.kind === "choice" && (
              <div className="course-choice-list" role="radiogroup" aria-label="选择一个方案">
                <p>先观察题目，再选一个你认为合适的办法。</p>
                {t.options?.map((option, index) => (
                  <button
                    key={`${t.id}-option-${index}`}
                    role="radio"
                    aria-checked={d.input === String(index)}
                    className={d.input === String(index) ? "selected" : ""}
                    disabled={d.feedback === "right"}
                    onClick={() => update({ input: String(index), feedback: null })}
                  >
                    <span className="choice-marker">{String.fromCharCode(65 + index)}</span>
                    <span>{option}</span>
                  </button>
                ))}
              </div>
            )}
            {t.kind === "count" && (
              <div className="shell-count">
                {Array.from({ length: t.a }, (_, i) => (
                  <span key={i}>🐚</span>
                ))}
              </div>
            )}
            {t.kind === "subtraction" && (
              <div className="shell-count subtraction-visual">
                {Array.from({ length: t.a + t.b }, (_, i) => (
                  <span key={i} className={i >= t.a ? "taken" : ""}>
                    🐚
                  </span>
                ))}
              </div>
            )}
            {t.kind === "place" && (
              <div className="place-visual">
                {Array.from({ length: t.a }, (_, i) => (
                  <div className="stick-bundle" key={i}>
                    {Array.from({ length: 10 }, (_, j) => (
                      <i key={j} />
                    ))}
                    <span>10根</span>
                  </div>
                ))}
                <div className="loose-sticks">
                  {Array.from({ length: t.b }, (_, i) => (
                    <i key={i} />
                  ))}
                </div>
              </div>
            )}
            {t.kind === "addition" && (
              <div className="addition-visual">
                <span>{"🐚".repeat(t.a)}</span>
                <b>＋</b>
                <span>{"🐚".repeat(t.b)}</span>
              </div>
            )}
            {t.kind === "application" && (
              <div className="relation-choice">
                <p>先想一想，这个委托是什么意思？</p>
                <div>
                  <button
                    className={d.relation === "add" ? "selected" : ""}
                    disabled={d.feedback === "right"}
                    onClick={() => update({ relation: "add", feedback: null })}
                  >
                    总共{t.a}个，分成{t.b}组
                  </button>
                  <button
                    className={d.relation === "multiply" ? "selected" : ""}
                    disabled={d.feedback === "right"}
                    onClick={() =>
                      update({ relation: "multiply", feedback: null })
                    }
                  >
                    有{t.a}组，每组{t.b}个
                  </button>
                </div>
              </div>
            )}
            {t.kind !== "choice" && <div className="equation">
              {t.kind === "recall"
                ? `${t.a} × ${t.b} =`
                : t.kind === "missing"
                  ? `${t.a} ×`
                  : "答案是"}
              <output
                className={d.feedback === "right" ? "correct" : ""}
                aria-label="输入的答案"
              >
                {d.input || <span>?</span>}
              </output>
              {t.kind === "missing" && `= ${t.a * t.b}`}
            </div>}
            {t.kind !== "choice" && <div className="keypad" aria-label="数字键盘">
              {[
                "1",
                "2",
                "3",
                "4",
                "5",
                "6",
                "7",
                "8",
                "9",
                "清空",
                "0",
                "⌫",
              ].map((v) => (
                <button
                  key={v}
                  disabled={d.feedback === "right"}
                  aria-label={
                    v === "⌫"
                      ? "删除一位"
                      : v === "清空"
                        ? "清空答案"
                        : `数字${v}`
                  }
                  onClick={() =>
                    v === "清空"
                      ? update({ input: "", feedback: null })
                      : num(v)
                  }
                >
                  {v}
                </button>
              ))}
            </div>}
          </div>
        )}
        {d.hint && (
          <div className="hint-panel">
            <Lightbulb size={20} />
            <div>
              <strong>{d.helpLevel > 1 ? "泡泡示范了一步" : "泡泡给你一个小提示"}</strong>
              <p>
                {(d.helpLevel > 1 ? t.workedText : t.hintText) || (t.foundation
                  ? t.kind === "count"
                    ? "用手指一个一个点着数，每个贝壳只数一次。"
                    : t.kind === "place"
                      ? `${t.a}个十是${t.a * 10}，再加${t.b}个一。`
                      : t.kind === "subtraction"
                        ? `划掉的是拿走的${t.b}颗，只数还留下的。`
                        : `从${t.a}开始，再往后数${t.b}个。`
                  : strategy(t.a, t.b))}
              </p>
              {!t.foundation && !["choice", "share", "interactive"].includes(t.kind) && (
                <>
                  <div className="hint-array">
                    {Array.from({ length: t.a }, (_, i) => (
                      <div key={i}>
                        {Array.from({ length: t.b }, (_, j) => (
                          <i key={j} />
                        ))}
                      </div>
                    ))}
                  </div>
                  <p>
                    {t.a}组，每组{t.b}个。{rhyme(t.a, t.b)}。
                  </p>
                </>
              )}
            </div>
          </div>
        )}
        <div className="lesson-feedback" aria-live="polite" ref={feedbackRef}>
          {d.feedback === "wrong" && (
            <p className="wrong-feedback">
              {wrongGuidance(t, d.relation)}
            </p>
          )}
          {d.feedback === "right" && (
            <div className="success-feedback">
              <span className="success-icon">
                <Check />
              </span>
              <div>
                <strong>
                  {successPraise}
                </strong>
                <p>{mathFeedback(t)}</p>
                {t.evidenceRole && t.evidenceRole !== "recognition" && <p className="sentence-frame">可以试着向泡泡解释：“{mathFeedback(t)}”</p>}
                {!audioError && (
                  <button
                    className="feedback-listen"
                    onClick={() =>
                      void speakSequence(successAudio).then((ok) =>
                        setAudioError(!ok),
                      )
                    }
                  >
                    <Volume2 size={16} />
                    听完整反馈
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </section>
      <footer className="lesson-footer">
        <button
          className="hint-button"
          onClick={() => {
            const nextLevel = Math.min(2, d.helpLevel + 1) as 1 | 2;
            update({ hint: true, helpLevel: nextLevel });
            const key = hintAudioKey(t);
            if (nextLevel === 1 && key && t.hintText)
              void speak(t.hintText, key).then((ok) => setAudioError(!ok));
          }}
          disabled={d.feedback === "right" || d.helpLevel >= 2}
        >
          <Lightbulb size={20} />
          {d.helpLevel === 0 ? "请泡泡帮忙" : d.helpLevel === 1 ? "再看一步示范" : "我们一起想过啦"}
        </button>
        {d.feedback === "right" ? (
          <button className="primary next-button" onClick={proceed}>
            {s.index + 1 === s.tasks.length ? "完成小委托" : "继续冒险"}
            <ArrowRight size={20} />
          </button>
        ) : (
          <button
            className="primary next-button"
            onClick={check}
            disabled={
              (!visual && !d.input) || (t.kind === "application" && !d.relation)
            }
          >
            我完成了 <Check size={20} />
          </button>
        )}
        {assessed && d.feedback !== "right" && (
          <button
            className="text-button"
            onClick={() => {
              if (!d.attempt) {
                store.add("answer", {
                  task: t,
                  value: -1,
                  firstAnswer: -1,
                  correct: false,
                  hinted: false,
                  attempt: 1,
                  duration: Date.now() - d.started,
                  session: s.id,
                });
              }
              proceed();
            }}
          >
            这题暂时不会
          </button>
        )}
      </footer>
      {exit && (
        <div className="modal-overlay">
          <section
            className="modal small"
            role="dialog"
            aria-modal="true"
            aria-label="保存冒险"
          >
            <button
              className="modal-close icon-button"
              aria-label="关闭"
              onClick={() => setExit(false)}
            >
              <X />
            </button>
            <Animal size={100} />
            <h2>小岛会等你回来</h2>
            <p>已经走过的路和现在这道题，都会帮你记好。</p>
            <button className="primary full" onClick={onExit}>
              保存，回到小岛
            </button>
            <button className="text-button full" onClick={() => setExit(false)}>
              我还想继续
            </button>
          </section>
        </div>
      )}
    </main>
  );
}
